import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { VerificationTitleAndInfo } from '../../../kyc-verification/components/verification-title-and-info/verification-title-and-info';
import { DocumentUploadSectionContainer } from '../../../kyc-verification/containers/document-upload/document-upload-section/document-upload-section';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { exists } from '../../../shared/utils/filters';
import { slugify } from '../../../shared/utils/strings';
import { useGetUserProfileQuery } from '../../queries/profile.graphql';
import {
    IUserProfilesEnumsVerificationStatus,
    useGetUserVerificationDocumentsQuery,
    useDocumentsUpdatedSubscription,
} from '../../queries/verification.graphql';

export const UserVerification: FunctionComponent = () => {
    const auth = useAuth();
    const kycConfigSettings = config.KYC.SECTIONS;

    const userProfileId = auth.user?.profile?.user_profile_id as string;

    const [{ data: userProfiledata }] = useGetUserProfileQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId,
        },
    });

    const [{ data: userVerificationDocumentsData }, reload] = useGetUserVerificationDocumentsQuery({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId,
        },
        requestPolicy: 'cache-and-network',
    });

    const [{ data: documentsChange }] = useDocumentsUpdatedSubscription({
        pause: !userProfileId,
        variables: {
            xTenantID: config.TENANT_ID,
            userProfileId: userProfileId,
        },
    });

    useEffect(() => {
        if (!documentsChange) {
            return;
        }

        reload();
    }, [reload, documentsChange]);

    const userProfile = userProfiledata?.userProfile;
    const allUserVerificationDocuments = userVerificationDocumentsData?.userDocuments;

    return (
        <div>
            <VerificationTitleAndInfo
                translationId={'account.verification.account'}
                translationDefault={'Account'}
                accountStatus={
                    userProfile?.compliance?.kycVerificationStatus
                        ? (userProfile?.compliance
                              ?.kycVerificationStatus as IUserProfilesEnumsVerificationStatus)
                        : null
                }
            />

            {kycConfigSettings?.map((item) => {
                return (
                    <DocumentUploadSectionContainer
                        key={item.type}
                        kycType={item.type}
                        reload={reload}
                        title={
                            <FormattedMessage
                                id={`account.verification.type.${slugify(item.type)}`}
                                defaultMessage={item.type}
                            />
                        }
                        verificationStatus={
                            (userProfile?.compliance?.[
                                item.type as 'idVerificationStatus'
                            ] as IUserProfilesEnumsVerificationStatus) ??
                            IUserProfilesEnumsVerificationStatus.NotSet
                        }
                        documentTypes={item.documents}
                        userUploadedDocuments={allUserVerificationDocuments?.filter(exists) ?? []}
                    />
                );
            })}
        </div>
    );
};
